<template>
  <v-dialog
    key="ready-modal"
    class="modal-layout ma-0"
    persistent
    scrollable
    data-testid="modal-layout"
    :model-value="true"
    :max-width="maxWidth"
    :fullscreen="fullscreenDialog"
    @click:outside="widgetCommunicatorService.closeModal()"
  >
    <v-card class="layout-wrapper" :data-testid="`step-${activeStepName}`">
      <v-app-bar
        v-show="props.showHeader"
        id="step-header"
        color="app-bars"
        :elevation="3"
        :class="{ 'unset-height-app-bar': $slots.alert }"
      >
        <v-col class="pa-0">
          <slot name="alert" />
          <slot name="header" />
        </v-col>
      </v-app-bar>

      <div id="step-container">
        <v-row
          id="step-container-body"
          justify="center"
          align="center"
          class="ma-0 h-100"
        >
          <v-col
            cols="12"
            align="center"
            justify="center"
            :class="innerClasses"
          >
            <slot name="body" />
          </v-col>
        </v-row>
      </div>

      <div v-if="$slots.chat" class="modal-chat">
        <div id="chat">
          <slot name="chat" />
        </div>
      </div>
      <v-footer
        v-show="props.showFooter"
        id="step-footer"
        ref="footer"
        :elevation="3"
        color="app-bars"
      >
        <slot name="footer" />
      </v-footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useDisplay } from "vuetify";
import { WidgetCommunicatorService } from "#src/services/widget-communicator.service";

import { storeToRefs } from "pinia";
import { useInstanceSettingsStore } from "#src/stores/instance-settings";
import { useFlowStore } from "#src/stores/flow";
import { useStepCriticalFailure } from "#src/stores/step-critical-failure";

import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from "vue";
import { APPLY_ROUTE_META_NAMES } from "#src/enumerations/step-enumeration";

const props = defineProps({
  showFooter: Boolean,
  showHeader: Boolean,
});
const pinia = inject("pinia");

const { mdAndDown } = useDisplay();
const scrollHeight = ref(600);
const widgetCommunicatorService = new WidgetCommunicatorService();

const criticalFailureStep = useStepCriticalFailure(pinia);
const flow = useFlowStore(pinia);
const instance = useInstanceSettingsStore(pinia);

const { criticalFailureReason } = storeToRefs(criticalFailureStep);
const { activeStep } = storeToRefs(flow);
const { useFullscreen } = storeToRefs(instance);

const fullscreenDialog = computed(() => {
  if (criticalFailureReason.value) return false;
  return useFullscreen.value || mdAndDown.value;
});

const activeStepName = computed(
  () => APPLY_ROUTE_META_NAMES[activeStep.value?.step]
);
const maxWidth = computed(() => {
  if (criticalFailureReason.value) return "40rem";
  if (mdAndDown.value || useFullscreen.value) return "100%";
  return "70rem";
});

const innerClasses = computed(() => {
  if (activeStep.value?.isFullscreen) {
    if (!useFullscreen.value) {
      return "no-line-height align-self-stretch pa-0";
    }
    return "h-100 pa-0 no-line-height w-100";
  }
  if (mdAndDown.value) return "px-0";
  return "";
});

const verticallySmall = computed(() => {
  return scrollHeight.value < 700;
});

watch(
  verticallySmall,
  val => {
    if (useFullscreen.value === val) return;
    useFullscreen.value = val;
  },
  { immediate: true }
);

onMounted(() => {
  storeScrollHeight();
  window.addEventListener("resize", storeScrollHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", storeScrollHeight);
});
function storeScrollHeight() {
  scrollHeight.value = document.body.scrollHeight;
}
</script>

<style lang="scss">
.modal-layout {
  #chat {
    position: absolute;
    right: 0;
    bottom: 0;
    transform-origin: bottom right;
    transform: translate(-15px, -90px);
  }

  .layout-wrapper {
    display: flex;
    flex-direction: column;
  }

  header.v-toolbar {
    z-index: 10;
    position: relative !important;
    flex: 0 0 auto;
  }

  footer.v-footer {
    width: 100%;
    position: relative !important;
    flex: 0 0 auto;
  }

  #step-container {
    flex: 1 1 auto;
    overflow-y: scroll;
  }

  #step-container-body {
    min-height: min(calc(100vh - 64px), 500px);
  }
}
</style>
